// Project Sentinel - Global Time Period Context
// Provides consistent time period selection across all pages
import { createContext, useContext, useState, useMemo, useEffect } from 'react'
import { useOptionalCustomerSettings } from './customer-context'
import { fiscalYearToDate, toLocalDateString } from '@/lib/local-date'
import { useTenantZone } from '@poiesis/locale'

// TimePeriodValue matches the UnifiedTimePeriodSelector interface
export interface TimePeriodValue {
  periodKey: string
  start: Date
  end: Date
}

// Context type definition
interface TimePeriodContextType {
  period: TimePeriodValue
  setPeriod: (period: TimePeriodValue) => void
  dateRange: { start_date: string; end_date: string }
  /** false while the period is FY to date and the fiscal year start is still loading: its dates are not final */
  isPeriodReady: boolean
}

// Storage key
const STORAGE_KEY = 'sentinel_time_period'

// Default period: FY to Date
function getDefaultPeriod(): TimePeriodValue {
  // Try to load from localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        periodKey: parsed.periodKey,
        start: new Date(parsed.start),
        end: new Date(parsed.end),
      }
    }
  } catch (error) {
    console.warn('Failed to load stored time period:', error)
  }

  // Default to FY to Date (end at yesterday — no intra-day data). Jan 1 is only a placeholder:
  // the provider anchors fytd to the customer's fiscal start month once the settings are known.
  return { periodKey: 'fytd', ...fiscalYearToDate(1) }
}

// Create context with undefined initial value (will be set by provider)
const TimePeriodContext = createContext<TimePeriodContextType | undefined>(undefined)

// Provider props
type TimePeriodProviderProps = {
  children: React.ReactNode
}

// Provider component
export function TimePeriodProvider({ children }: TimePeriodProviderProps) {
  const [storedPeriod, setPeriodState] = useState<TimePeriodValue>(getDefaultPeriod)
  const customer = useOptionalCustomerSettings()
  const fiscalStartMonth = customer?.settings?.fiscalYearStartMonth ?? null
  const isPeriodReady = !(storedPeriod.periodKey === 'fytd' && customer?.isLoading)

  // FY to date is anchored here, during render, as soon as the fiscal start month is known: a page
  // reading dateRange in the same render already gets the fiscal dates (the selector's own re-anchor
  // runs in an effect, one render later, and a page fetching in between asked for Jan 1 - yesterday).
  // SEN-433 T7: the days are the TENANT calendar's (Poiesis's fytd rule), so the tenant zone arriving re-anchors too.
  const tenantZone = useTenantZone()
  const period = useMemo<TimePeriodValue>(() => {
    if (storedPeriod.periodKey !== 'fytd' || fiscalStartMonth === null) return storedPeriod
    const { start, end } = fiscalYearToDate(fiscalStartMonth, new Date(), tenantZone)
    return start.getTime() === storedPeriod.start.getTime() && end.getTime() === storedPeriod.end.getTime()
      ? storedPeriod
      : { periodKey: 'fytd', start, end }
  }, [storedPeriod, fiscalStartMonth, tenantZone])

  // Persist to localStorage whenever period changes; never the Jan 1 placeholder of an unanchored fytd
  useEffect(() => {
    if (period.periodKey === 'fytd' && fiscalStartMonth === null) return
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          periodKey: period.periodKey,
          start: period.start.toISOString(),
          end: period.end.toISOString(),
        })
      )
    } catch (error) {
      console.warn('Failed to save time period to localStorage:', error)
    }
  }, [period, fiscalStartMonth])

  const setPeriod = (newPeriod: TimePeriodValue) => {
    setPeriodState(newPeriod)
  }

  // Memoize date range in API-compatible format
  // Local calendar days: toISOString() would turn Copenhagen's midnight of Sep 1 into "08-31"
  const startDate = toLocalDateString(period.start)
  const endDate = toLocalDateString(period.end)
  const dateRange = useMemo(() => ({ start_date: startDate, end_date: endDate }), [startDate, endDate])

  const value = useMemo(
    () => ({
      period,
      setPeriod,
      dateRange,
      isPeriodReady,
    }),
    [period, dateRange, isPeriodReady]
  )

  return <TimePeriodContext.Provider value={value}>{children}</TimePeriodContext.Provider>
}

// Hook to use time period context
export function useTimePeriod() {
  const context = useContext(TimePeriodContext)

  if (context === undefined) {
    throw new Error('useTimePeriod must be used within a TimePeriodProvider')
  }

  return context
}

/** The period when a provider is mounted, else undefined (a surface that only passes the period on, never needs it). */
export function useOptionalTimePeriod() {
  return useContext(TimePeriodContext)
}
